import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Edit3, Trash2, FolderKanban, ChevronUp, ChevronDown, ShieldCheck, AlertTriangle, RefreshCcw, Trash } from "lucide-react";
import { useDoubleConfirm } from "@/components/ConfirmProvider";

const ROLE_LABELS = { ADMIN: "Amministratore", AMMINISTRAZIONE: "Amministrazione", USER: "Utente" };
const ROLE_COLORS = { ADMIN: "bg-blue-100 text-blue-700", AMMINISTRAZIONE: "bg-emerald-100 text-emerald-700", USER: "bg-slate-100 text-slate-700" };

// Sprint UI-GLOBALE — pagine singole aperte dai riquadri di Impostazioni (Aziende e Utenti hanno pagine proprie).
const SEZIONI = {
  categories: { titolo: "Categorie documenti", sotto: "Categorie e sottocategorie documenti." },
  integrity: { titolo: "Integrità archivio", sotto: "Verifica sincronia tra app e Dropbox." },
};

export default function Settings() {
  const [params] = useSearchParams();
  const tab = SEZIONI[params.get("tab")] ? params.get("tab") : "categories";
  const [categories, setCategories] = useState([]);
  const loadAll = async () => {
    const cat = await api.get("/categories");
    setCategories(cat.data);
  };
  useEffect(() => { if (tab === "categories") loadAll(); }, [tab]);

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <Link to="/settings" className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="settings-back-link">
          <ArrowLeft className="h-4 w-4" /> Impostazioni
        </Link>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight mt-1" data-testid="settings-section-title">{SEZIONI[tab].titolo}</h1>
        <p className="text-slate-500 mt-1">{SEZIONI[tab].sotto}</p>
      </div>
      {tab === "categories"
        ? <CategoriesSection categories={categories} reload={loadAll} canEdit={true} canDelete={true} />
        : <IntegritySection />}
    </div>
  );
}

/* ========================= Pratiche ========================= */
/* ========================= Categories ========================= */
function CategoriesSection({ categories, reload, canEdit, canDelete }) {
  const confirm = useDoubleConfirm();
  const [catOpen, setCatOpen] = useState(false);
  const [catEdit, setCatEdit] = useState(null);
  const [catName, setCatName] = useState("");
  const [subOpen, setSubOpen] = useState(false);
  const [subEdit, setSubEdit] = useState(null);
  const [subCategory, setSubCategory] = useState(null);
  const [subName, setSubName] = useState("");

  const saveCat = async () => {
    try {
      if (catEdit) await api.put(`/categories/${catEdit.id}`, { name: catName });
      else await api.post("/categories", { name: catName });
      toast.success("Salvato"); setCatOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const removeCat = async (c) => {
    const ok = await confirm({ variant: "delete", detail: `Categoria: "${c.name}"` });
    if (!ok) return;
    try { await api.delete(`/categories/${c.id}`); reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const saveSub = async () => {
    try {
      if (subEdit) await api.put(`/subcategories/${subEdit.id}`, { name: subName, category_id: subCategory.id });
      else await api.post("/subcategories", { name: subName, category_id: subCategory.id });
      toast.success("Salvato"); setSubOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const removeSub = async (s) => {
    const ok = await confirm({ variant: "delete", detail: `Sottocategoria: "${s.name}"` });
    if (!ok) return;
    try { await api.delete(`/subcategories/${s.id}`); reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const moveCat = async (cat, dir) => {
    const ids = categories.map((c) => c.id);
    const idx = ids.indexOf(cat.id);
    const target = idx + dir;
    if (target < 0 || target >= ids.length) return;
    const newIds = [...ids];
    [newIds[idx], newIds[target]] = [newIds[target], newIds[idx]];
    try { await api.post("/categories/reorder", { ids: newIds }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const moveSub = async (cat, sub, dir) => {
    const subs = cat.subcategories;
    const ids = subs.map((s) => s.id);
    const idx = ids.indexOf(sub.id);
    const target = idx + dir;
    if (target < 0 || target >= ids.length) return;
    const newIds = [...ids];
    [newIds[idx], newIds[target]] = [newIds[target], newIds[idx]];
    try { await api.post("/subcategories/reorder", { category_id: cat.id, ids: newIds }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <Card className="p-6 border border-slate-200 shadow-sm bg-white">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-heading text-lg font-semibold">Categorie e sottocategorie</h2>
        {canEdit && <Button onClick={() => { setCatEdit(null); setCatName(""); setCatOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="new-category-btn"><Plus className="h-4 w-4 mr-1.5" /> Nuova categoria</Button>}
      </div>

      <div className="space-y-4">
        {categories.map((cat, catIdx) => (
          <div key={cat.id} className="border border-slate-200 rounded-lg">
            <div className="px-4 py-3 flex items-center gap-2 bg-slate-50 border-b border-slate-200">
              <FolderKanban className="h-4 w-4 text-blue-600" />
              <div className="font-medium flex-1">{cat.name}</div>
              {canEdit && (
                <div className="flex flex-col -my-1">
                  <button
                    onClick={() => moveCat(cat, -1)}
                    disabled={catIdx === 0}
                    className="text-slate-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Sposta su"
                    data-testid={`cat-up-${cat.id}`}
                  ><ChevronUp className="h-4 w-4" /></button>
                  <button
                    onClick={() => moveCat(cat, 1)}
                    disabled={catIdx === categories.length - 1}
                    className="text-slate-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Sposta giù"
                    data-testid={`cat-down-${cat.id}`}
                  ><ChevronDown className="h-4 w-4" /></button>
                </div>
              )}
              {canEdit && <Button variant="ghost" size="sm" onClick={() => { setCatEdit(cat); setCatName(cat.name); setCatOpen(true); }}><Edit3 className="h-4 w-4" /></Button>}
              {canEdit && <Button variant="ghost" size="sm" onClick={() => { setSubEdit(null); setSubCategory(cat); setSubName(""); setSubOpen(true); }}><Plus className="h-4 w-4" /></Button>}
              {canDelete && <Button variant="ghost" size="sm" onClick={() => removeCat(cat)} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>}
            </div>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {cat.subcategories.map((s, subIdx) => (
                <div key={s.id} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded border border-slate-100 text-sm group">
                  <div className="flex flex-col -my-1">
                    <button
                      onClick={() => moveSub(cat, s, -1)}
                      disabled={subIdx === 0}
                      className="text-slate-400 hover:text-blue-600 disabled:opacity-20"
                      title="Sposta su"
                    ><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button
                      onClick={() => moveSub(cat, s, 1)}
                      disabled={subIdx === cat.subcategories.length - 1}
                      className="text-slate-400 hover:text-blue-600 disabled:opacity-20"
                      title="Sposta giù"
                    ><ChevronDown className="h-3.5 w-3.5" /></button>
                  </div>
                  <span className="flex-1 truncate">{s.name}</span>
                  {canEdit && <button onClick={() => { setSubEdit(s); setSubCategory(cat); setSubName(s.name); setSubOpen(true); }} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-blue-600"><Edit3 className="h-3.5 w-3.5" /></button>}
                  {canDelete && <button onClick={() => removeSub(s)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
              {cat.subcategories.length === 0 && <div className="text-xs text-slate-400 py-1">Nessuna sottocategoria</div>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{catEdit ? "Modifica categoria" : "Nuova categoria"}</DialogTitle></DialogHeader>
          <div><Label className="text-xs">Nome</Label><Input value={catName} onChange={(e) => setCatName(e.target.value)} data-testid="category-name-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Annulla</Button>
            <Button onClick={saveCat} className="bg-blue-600 hover:bg-blue-700" data-testid="category-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{subEdit ? "Modifica sottocategoria" : "Nuova sottocategoria"} · {subCategory?.name}</DialogTitle></DialogHeader>
          <div><Label className="text-xs">Nome</Label><Input value={subName} onChange={(e) => setSubName(e.target.value)} data-testid="subcategory-name-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubOpen(false)}>Annulla</Button>
            <Button onClick={saveSub} className="bg-blue-600 hover:bg-blue-700" data-testid="subcategory-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ========================= Integrity ========================= */
function IntegritySection() {
  const confirm = useDoubleConfirm();
  const [loading, setLoading] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [data, setData] = useState(null);
  const [folders, setFolders] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/integrity-check"),
        api.get("/admin/dropbox-folders"),
      ]);
      setData(a.data);
      setFolders(b.data.folders);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore verifica integrità");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runCheck(); /* eslint-disable-next-line */ }, []);

  const deletePaths = async (paths, label) => {
    if (paths.length === 0) return;
    const ok = await confirm({
      variant: "delete",
      title: "Elimina da Dropbox",
      message: `Stai per eliminare definitivamente ${paths.length} ${label} da Dropbox. Questa azione può essere annullata entro 30 giorni dal cestino Dropbox.`,
      detail: paths.slice(0, 5).join("\n") + (paths.length > 5 ? `\n... e altri ${paths.length - 5}` : ""),
      confirmLabel: "SI, elimina",
    });
    if (!ok) return;
    setCleaningUp(true);
    try {
      const res = await api.post("/admin/dropbox-cleanup", { paths });
      const okCount = res.data.results.filter(r => r.ok).length;
      const failCount = res.data.results.filter(r => !r.ok).length;
      toast.success(`Eliminati ${okCount} / ${paths.length}${failCount ? ` (${failCount} errori)` : ""}`);
      await runCheck();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore pulizia");
    } finally {
      setCleaningUp(false);
    }
  };

  return (
    <Card className="p-6 border border-slate-200 shadow-sm bg-white">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" /> Integrità archivio Dropbox
          </h2>
          <p className="text-sm text-slate-500 mt-1">Verifica che ogni documento presente nell'app sia anche su Dropbox e viceversa.</p>
        </div>
        <Button
          onClick={runCheck}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="integrity-check-btn"
        >
          <RefreshCcw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Controllo..." : "Esegui verifica"}
        </Button>
      </div>

      {!data && !loading && (
        <div className="text-sm text-slate-500 py-8 text-center">Premi "Esegui verifica" per iniziare</div>
      )}
      {loading && (
        <div className="text-sm text-slate-500 py-8 text-center">Controllo in corso...</div>
      )}

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-medium">In sincronia</div>
              <div className="text-2xl font-semibold text-emerald-900 mt-1" data-testid="integrity-in-sync">{data.in_sync}</div>
              <div className="text-xs text-emerald-700 mt-1">file allineati</div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="text-xs uppercase tracking-wider text-amber-700 font-medium">Solo su Dropbox</div>
              <div className="text-2xl font-semibold text-amber-900 mt-1" data-testid="integrity-orphans-count">{data.orphans_on_dropbox.length}</div>
              <div className="text-xs text-amber-700 mt-1">file orfani</div>
            </div>
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
              <div className="text-xs uppercase tracking-wider text-rose-700 font-medium">Mancanti su Dropbox</div>
              <div className="text-2xl font-semibold text-rose-900 mt-1" data-testid="integrity-missing-count">{data.missing_on_dropbox.length}</div>
              <div className="text-xs text-rose-700 mt-1">documenti senza file</div>
            </div>
          </div>

          {/* Orphans on Dropbox */}
          {data.orphans_on_dropbox.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="font-semibold flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4" /> File su Dropbox non presenti in KARGEST
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={cleaningUp}
                  onClick={() => deletePaths(data.orphans_on_dropbox.map(f => f.path), "file orfani")}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="integrity-cleanup-orphans-btn"
                >
                  <Trash className="h-4 w-4 mr-1.5" /> Elimina tutti
                </Button>
              </div>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-80 overflow-y-auto">
                {data.orphans_on_dropbox.map((f) => (
                  <div key={f.path} className="px-3 py-2 text-xs flex items-center justify-between gap-2">
                    <span className="truncate flex-1 font-mono text-slate-700">{f.path}</span>
                    <span className="text-slate-500 shrink-0">{(f.size / 1024).toFixed(1)} KB</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Missing on Dropbox */}
          {data.missing_on_dropbox.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold flex items-center gap-2 text-rose-800 mb-2">
                <AlertTriangle className="h-4 w-4" /> Documenti nell'archivio ma non su Dropbox
              </h3>
              <div className="border border-rose-200 rounded-lg divide-y divide-rose-100 max-h-80 overflow-y-auto bg-rose-50/30">
                {data.missing_on_dropbox.map((d) => (
                  <div key={d.id} className="px-3 py-2 text-xs">
                    <div className="font-medium text-slate-800 truncate">{d.display_name}</div>
                    <div className="font-mono text-[11px] text-slate-500 truncate">{d.storage_path}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Questi documenti non possono essere scaricati. Valuta di eliminarli dall'archivio o ricaricare i file.</p>
            </div>
          )}

          {/* Dropbox root folders */}
          {folders && folders.filter(f => !f.known).length > 0 && (
            <div className="mb-2">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="font-semibold flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4" /> Cartelle radice non collegate ad aziende
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={cleaningUp}
                  onClick={() => deletePaths(folders.filter(f => !f.known).map(f => `KARGEST/${f.name}`), "cartelle")}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="integrity-cleanup-folders-btn"
                >
                  <Trash className="h-4 w-4 mr-1.5" /> Elimina tutte
                </Button>
              </div>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {folders.filter(f => !f.known).map((f) => (
                  <div key={f.name} className="px-3 py-2 text-sm font-medium text-slate-800">
                    <span className="font-mono text-slate-600">/KARGEST/</span>{f.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.orphans_on_dropbox.length === 0 && data.missing_on_dropbox.length === 0 &&
            folders && folders.filter(f => !f.known).length === 0 && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" /> Archivio perfettamente allineato con Dropbox
            </div>
          )}
        </>
      )}
    </Card>
  );
}

